import cn from "classnames";
import { organizationFlagsEnum } from "helpers/enums";
import { store } from "helpers/filtersStorage";
import { paths } from "helpers/paths";
import { isRegionDryingPeriodsEnabledForClients } from "helpers/regionDryingPeriods";
import { REPORT_TYPES } from "helpers/reports";
import { ymEvent } from "helpers/system";
import pick from "lodash/pick";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { HOURLY_REPORT_STARTED_AFTER } from "../";
import AccountsReceivableFilters from "./accountsReceivableFilters";
import ByEndDateFilter from "./byEndDate";
import CarTypeFilter from "./carType";
import GroupByBidLogistFilter from "./groupByBidLogist";
import GroupedByBidId from "./groupedByBidId";
import InternationalBidsFilter from "./internationalBids";
import LegalPersonFilter from "./legalPerson";
import LogistFilter from "./logist";
import MonthYearFilter from "./monthYearFilter";
import MultipleCarTypeFilter from "./multipleCarType";
import MultiTrailerTypeFilter from "./multiTrailerType";
import MultiTransportColumnFilter from "./multiTransportColumn";
import NdsTypeFilter from "./ndsType";
import OnlyWithoutApp from "./onlyWithoutApp";
import OrganizationFilter from "./organization";
import PeriodFilter from "./period";
import PointIntersectionsFilters from "./pointIntersections";
import PointsFilter from "./points";
import ResponsibleFilter from "./responsible";
import SaveStateFilter from "./saveStateFilter";
import SelectFilter from "./selectFilter";
import ToggleFilter from "./toggleFilter";
import ToggleInitialPlanValues from "./toggleInitialPlanValues";
import ToggleWithNds from "./toggleWithNds";
import TrailerTypeFilter from "./trailerType";
import TransportColumnFilter from "./transportColumn";

const dryingPeriodFilters = ["filterByDryingPeriods", "excludeBidsWithSeasonalRestrictions"];

const filtersForSaving = [
    "withInternationalBids",
    "withoutInternationalBids",
    "sumWithoutVat",
    "carTypeId",
    "trailerTypeId",
    "multipleCarTypeId",
    "transportColumnId",
    "responsibleId",
    "onlyWithoutApp",
    "filterOnlyByEndDate",
    "groupByBidLogist",
    "firstPoint",
    "lastPoint",
    "groupByRegion",
    "groupByCreator",
    "marginalityValues",
    "organizationId",
    "legalPersonId",
    "month",
    "year",
    "reportPeriod",
    "parametersObject",
    "byEmptyMileage",
    "showFullMileages",
    "filterByResponsibleIsNotLogist",
    "withoutResponse",
    "type",
    "groupByDates",
    "isAccountsPayable",
    "source",
    "groupPeriodsByCar",
    "useRefuelingTransactions",
    "logistIds",
    "trailerTypeIds",
    "transportColumnIds",
    ...(isRegionDryingPeriodsEnabledForClients ? dryingPeriodFilters : []),
];

export const componentFiltersMapper = {
    [REPORT_TYPES.DriverAppInstallReport]: ["onlyWithoutApp"],
    [REPORT_TYPES.ExcessMileageReport]: [
        "responsibleId",
        "transportColumnId",
        "groupByBidLogist",
        "filterOnlyByEndDate",
    ],
    [REPORT_TYPES.OverallReport]: [
        "multipleCarTypeId",
        "transportColumnId",
        "legalPersonId",
        "groupByCurrentLogist",
    ],
    [REPORT_TYPES.TrailersReport]: ["legalPersonId"],
    [REPORT_TYPES.MonthlyOverallReport]: ["reportPeriod", "saveState", "groupByCurrentLogist"],
    [REPORT_TYPES.OverallReportWithVat]: [
        "withInternationalBids",
        "withoutInternationalBids",
        "sumWithoutVat",
        "carTypeId",
    ],
    [REPORT_TYPES.CounterpartiesReport]: ["firstPoint", "lastPoint", "trailerTypeId", "legalPersonId"],
    [REPORT_TYPES.CitiesReport]: ["groupByRegion", "logistIds", "trailerTypeIds", "transportColumnIds"],
    [REPORT_TYPES.BidsCreatedOnTimeReport]: ["groupByCreator"],
    [REPORT_TYPES.BidsWithRegionalFramesReport]: isRegionDryingPeriodsEnabledForClients ? dryingPeriodFilters : [],
    [REPORT_TYPES.EmptyMileageReport]: ["showFullMileages"],
    [REPORT_TYPES.PlanFactReport]: ["byEmptyMileage", "showFullMileages"],
    [REPORT_TYPES.MarginalityReport]: ["marginalityValues"],
    [REPORT_TYPES.OrganizationActivityReport]: ["organizationId"],
    [REPORT_TYPES.PlanPointIntersectionsReport]: ["parametersObject"],
    [REPORT_TYPES.ResponsibleLogistsReport]: ["filterByResponsibleIsNotLogist"],
    [REPORT_TYPES.SuitableOrdersReport]: ["withoutResponse"],
    [REPORT_TYPES.ClientStatisticsReport]: ["type"],
    [REPORT_TYPES.AccountsReceivableReport]: ["groupByDates", "isOverdue", "isAccountsPayable", "source"],
    [REPORT_TYPES.PlanFactRefuelingsReport]: ["groupPeriodsByCar", "useRefuelingTransactions"],
    [REPORT_TYPES.RefuelingsReport]: ["useRefuelingTransactions"],
};

export default function Filters(props) {
    const {
        getItem,
        id,
        filterData,
        forceUpdateReport,
        state,
        setState,
        hourlyAnalyzeEnabled,
        enums,
        switchMap,
        history,
        profile,
        organizationTimezone,
        organization,
    } = props;
    const [filtersOpened, setFiltersOpened] = useState(false);
    const expandCname = cn("b-filter__collapse-btn", {
        "b-filter__collapse-btn--top": filtersOpened,
        "b-filter__collapse-btn--bottom": !filtersOpened,
    });

    const setFilter = ({ name, value }) => {
        setState((state) => {
            state[name] = value;
            store("reports", pick(state, filtersForSaving));
            return state;
        });
    };

    const setDateValue = (name, value) => {
        setState((state) => {
            value = moment(value);
            if (!hourlyAnalyzeEnabled || value < moment(HOURLY_REPORT_STARTED_AFTER)) {
                value = name === "startDate" ? value.startOf("day") : value.endOf("day");
            }
            if (name === "startDate" && state.endDate < value) {
                state.endDate = moment(value).add(1, "day").endOf("day");
            }
            state[name] = value;
            return state;
        });
    };

    const filterComponents = useMemo(() => {
        switch (id) {
            case REPORT_TYPES.PlanPointIntersectionsReport:
                return [PointIntersectionsFilters];
            case REPORT_TYPES.ExcessMileageReport:
                return [
                    PeriodFilter,
                    TransportColumnFilter,
                    ResponsibleFilter,
                    GroupByBidLogistFilter,
                    ByEndDateFilter,
                ];
            case REPORT_TYPES.OverallReport:
                return [PeriodFilter, MultipleCarTypeFilter, TransportColumnFilter, LegalPersonFilter];
            case REPORT_TYPES.TrailersReport:
                return [PeriodFilter, LegalPersonFilter];
            case REPORT_TYPES.MonthlyOverallReport:
                return [MonthYearFilter, SaveStateFilter];
            case REPORT_TYPES.OverallReportWithVat:
                return [PeriodFilter, CarTypeFilter, NdsTypeFilter, InternationalBidsFilter];
            case REPORT_TYPES.CounterpartiesReport:
                return [PeriodFilter, PointsFilter, TrailerTypeFilter, LegalPersonFilter];
            case REPORT_TYPES.OrganizationActivityReport:
                return [PeriodFilter, OrganizationFilter];
            case REPORT_TYPES.DriverAppInstallReport:
                return [OnlyWithoutApp];
            case REPORT_TYPES.CitiesReport:
                return [PeriodFilter, LogistFilter, MultiTrailerTypeFilter, MultiTransportColumnFilter];
            case REPORT_TYPES.CarsWithoutBidsReport:
            case REPORT_TYPES.DriversStatsOfYearReport:
            case REPORT_TYPES.FuelCardsPaymentReport:
            case REPORT_TYPES.ClientStatisticsReport:
                return [];
            default:
                return [PeriodFilter];
        }
    }, [id]);
    const hasPeriodFilter = filterComponents.includes(PeriodFilter);
    const isValidDateValue = (value) => Boolean(value) && moment(value).isValid();
    const hasValidPeriod = isValidDateValue(state.startDate) && isValidDateValue(state.endDate);
    const refreshDisabled = hasPeriodFilter && !hasValidPeriod;

    const renderReportSpecificFilters = () => {
        const filterProps = {
            setFilter,
            filterData,
            id,
            hourlyAnalyzeEnabled,
            setDateValue,
            values: state,
            enums,
            switchMap,
            history,
            profile,
            organizationTimezone,
        };
        return filterComponents.map((Comp, i) => <Comp key={i} {...filterProps} values={state} />);
    };

    const defaultFields = () => {
        return (
            <>
                <div className="report__filters--left">
                    {renderReportSpecificFilters()}
                    {defaultBtn()}
                </div>
                <div className="report__filters--right ml-auto justify-content-end align-items-center text-right">
                    {REPORT_TYPES.CarIdlesReport === id && (
                        <GroupedByBidId value={state.bidId} filterData={filterData} />
                    )}
                    {REPORT_TYPES.EmptyMileageReport === id && (
                        <ToggleFilter
                            name="showFullMileages"
                            label="Показывать весь пробег заявки"
                            value={state.showFullMileages}
                            setFilter={setFilter}
                            getItem={getItem}
                            forceUpdateReport={forceUpdateReport}
                        />
                    )}
                    {REPORT_TYPES.PlanFactReport === id && (
                        <>
                            <ToggleInitialPlanValues value={state.showInitialPlanValues} filterData={filterData} />
                            <ToggleFilter
                                name="byEmptyMileage"
                                label="План-факт по порожним заявкам"
                                value={state.byEmptyMileage}
                                testId="toggle-empty-mileage"
                                setFilter={setFilter}
                                getItem={getItem}
                                forceUpdateReport={forceUpdateReport}
                            />
                            <ToggleFilter
                                name="showFullMileages"
                                label="Отображать весь пробег заявок"
                                value={state.showFullMileages}
                                testId="toggle-mileages"
                                setFilter={setFilter}
                                getItem={getItem}
                                forceUpdateReport={forceUpdateReport}
                            />
                        </>
                    )}
                    {REPORT_TYPES.AccountsReceivableReport === id && (
                        <>
                            {!state.demoData && (
                                <AccountsReceivableFilters
                                    values={state}
                                    setFilter={setFilter}
                                    getItem={getItem}
                                    forceUpdateReport={forceUpdateReport}
                                />
                            )}
                            <ToggleFilter
                                name="groupByDates"
                                label="Группировать по датам оплаты"
                                value={state.groupByDates}
                                testId="toggle-group-by-date"
                                setFilter={setFilter}
                                getItem={getItem}
                                forceUpdateReport={forceUpdateReport}
                                onBeforeChange={(val) => {
                                    if (!val) {
                                        ymEvent("GroupByDebtPaymentDates");
                                    }
                                }}
                            />
                            <ToggleFilter
                                name="isOverdue"
                                label="Только просроченные"
                                value={state.isOverdue}
                                testId="toggle-is-overdue"
                                setFilter={setFilter}
                                getItem={getItem}
                                forceUpdateReport={forceUpdateReport}
                                tooltip={
                                    <>
                                        В фильтр попадают заявки с просрочкой по оплате, <br />а также при поступлении
                                        платежа после плановой даты оплаты
                                    </>
                                }
                            />
                        </>
                    )}
                    {[
                        REPORT_TYPES.OverallReport,
                        REPORT_TYPES.TrailersReport,
                        REPORT_TYPES.MonthlyOverallReport,
                        REPORT_TYPES.CounterpartiesReport,
                    ].includes(id) && <ToggleWithNds value={state.overallReportWithNds} filterData={filterData} />}
                    {REPORT_TYPES.SuitableOrdersReport === id && (
                        <ToggleFilter
                            name="withoutResponse"
                            label="Заказы без отклика"
                            value={state.withoutResponse}
                            testId="toggle-without-reponse"
                            setFilter={setFilter}
                            getItem={getItem}
                            forceUpdateReport={forceUpdateReport}
                        />
                    )}
                    {REPORT_TYPES.CitiesReport === id && (
                        <ToggleFilter
                            name="groupByRegion"
                            label="Группировать по регионам"
                            value={state.groupByRegion}
                            setFilter={setFilter}
                            getItem={getItem}
                            forceUpdateReport={forceUpdateReport}
                        />
                    )}
                    {REPORT_TYPES.BidsCreatedOnTimeReport === id && (
                        <ToggleFilter
                            name="groupByCreator"
                            label="Группировать по автору"
                            value={state.groupByCreator}
                            setFilter={setFilter}
                            getItem={getItem}
                            forceUpdateReport={forceUpdateReport}
                        />
                    )}
                    {isRegionDryingPeriodsEnabledForClients && REPORT_TYPES.BidsWithRegionalFramesReport === id && (
                        <ToggleFilter
                            name="filterByDryingPeriods"
                            label="Только в сезон просушки"
                            value={state.filterByDryingPeriods}
                            setFilter={setFilter}
                            getItem={getItem}
                            forceUpdateReport={forceUpdateReport}
                        />
                    )}
                    {isRegionDryingPeriodsEnabledForClients && REPORT_TYPES.BidsWithRegionalFramesReport === id && (
                        <ToggleFilter
                            name="excludeBidsWithSeasonalRestrictions"
                            label="Исключить заявки с запретом на сезонные ограничения"
                            value={state.excludeBidsWithSeasonalRestrictions}
                            setFilter={setFilter}
                            getItem={getItem}
                            forceUpdateReport={forceUpdateReport}
                        />
                    )}
                    {REPORT_TYPES.ResponsibleLogistsReport === id && (
                        <ToggleFilter
                            name="filterByResponsibleIsNotLogist"
                            label="Показать заявки, в которых ответственный не является логистом"
                            value={state.filterByResponsibleIsNotLogist}
                            setFilter={setFilter}
                            getItem={getItem}
                            forceUpdateReport={forceUpdateReport}
                        />
                    )}
                    {REPORT_TYPES.MarginalityReport === id && (
                        <SelectFilter
                            name="marginalityValues"
                            value={state.marginalityValues}
                            options={[
                                { value: "all", label: "Все значения" },
                                { value: "estimated", label: "Расчетные значения" },
                                { value: "fact", label: "Фактические значения" },
                            ]}
                            setFilter={setFilter}
                            getItem={getItem}
                            forceUpdateReport={forceUpdateReport}
                        />
                    )}
                    {REPORT_TYPES.ClientStatisticsReport === id && (
                        <SelectFilter
                            name="type"
                            value={state.type}
                            options={[
                                { value: "All", label: "Все" },
                                { value: "OnlyLogists", label: "Только логисты" },
                                { value: "OnlyOrganizations", label: "Только организации" },
                            ]}
                            setFilter={setFilter}
                            getItem={getItem}
                            forceUpdateReport={forceUpdateReport}
                        />
                    )}
                    {[REPORT_TYPES.PlanFactRefuelingsReport, REPORT_TYPES.RefuelingsReport].includes(id) &&
                        organization.flags.includes(organizationFlagsEnum.UseRefuelingTransactions) && (
                            <ToggleFilter
                                name="useRefuelingTransactions"
                                label="Данныe на основе транзакций"
                                value={state.useRefuelingTransactions}
                                testId="toggle-use-refueling-transactions"
                                setFilter={setFilter}
                                getItem={getItem}
                                forceUpdateReport={forceUpdateReport}
                            />
                        )}
                    {REPORT_TYPES.PlanFactRefuelingsReport === id && (
                        <>
                            <ToggleFilter
                                name="groupPeriodsByCar"
                                label="Группировать даты заявок по периоду"
                                value={state.groupPeriodsByCar}
                                testId="toggle-group-periods-by-car"
                                setFilter={setFilter}
                                getItem={getItem}
                                forceUpdateReport={forceUpdateReport}
                            />
                            <a
                                className="btn btn-sm btn-secondary"
                                target="_blank"
                                href={paths.reportsShow(REPORT_TYPES.RefuelingsVisitStatsReport)}
                                rel="noreferrer"
                            >
                                Статистика посещения АЗС
                            </a>
                        </>
                    )}
                </div>
            </>
        );
    };

    const defaultBtn = () => (
        <div className={cn("mb-md-0", { "report__filters--refresh": !filtersOpened })}>
            <button
                className={cn("btn btn-sm btn-brand", { disabled: refreshDisabled })}
                type="button"
                disabled={refreshDisabled}
                onClick={() => {
                    if (!refreshDisabled) {
                        getItem();
                    }
                }}
            >
                Обновить
            </button>
        </div>
    );

    return (
        <div className="report__filters-wrapper w-100">
            <div className="w-100 report-filters report__filters--mobile">
                <div className="b-filter__head" onClick={setFiltersOpened.bind(null, !filtersOpened)}>
                    <div className="b-filter__head--left">
                        {!filtersOpened && <div className="b-filter__filter-icon" />}
                        <div className={cn("b-filter__search-text", { "b-filter__search-text--dark": filtersOpened })}>
                            Фильтры
                        </div>
                    </div>
                    <div className="b-filter__head--right">
                        <div className={expandCname} />
                    </div>
                </div>
            </div>
            <div className={cn("report__filters-body", { "report__filters-body--show": filtersOpened })}>
                {defaultFields()}
            </div>
            <div className={cn("report__filters-footer", { "report__filters-footer--show": filtersOpened })}>
                {defaultBtn()}
            </div>
        </div>
    );
}
